import { useEffect, useRef } from 'react';

export const OrbBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Animation state
    let time = 0;

    const animate = () => {
      if (!canvas || !ctx) return;

      const width = canvas.width / (window.devicePixelRatio || 1);
      const height = canvas.height / (window.devicePixelRatio || 1);
      const centerX = width / 2;
      const centerY = height / 2;
      const size = Math.min(width, height);

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Create multiple gradient circles
      time += 0.005;

      // Main orb - larger, centered
      const gradient1 = ctx.createRadialGradient(
        centerX + Math.cos(time * 0.5) * size * 0.05,
        centerY + Math.sin(time * 0.5) * size * 0.05,
        size * 0.1,
        centerX,
        centerY,
        size * 0.45
      );
      gradient1.addColorStop(0, 'rgba(243, 156, 94, 0.65)'); // Orange - increased
      gradient1.addColorStop(0.4, 'rgba(255, 218, 118, 0.5)'); // Yellow - increased
      gradient1.addColorStop(0.7, 'rgba(73, 161, 167, 0.4)'); // Teal - increased
      gradient1.addColorStop(1, 'rgba(73, 161, 167, 0)');

      ctx.fillStyle = gradient1;
      ctx.fillRect(0, 0, width, height);

      // Secondary orb - rotates around
      const angle1 = time * 0.8;
      const orb2X = centerX + Math.cos(angle1) * size * 0.15;
      const orb2Y = centerY + Math.sin(angle1) * size * 0.15;

      const gradient2 = ctx.createRadialGradient(
        orb2X,
        orb2Y,
        size * 0.05,
        orb2X,
        orb2Y,
        size * 0.25
      );
      gradient2.addColorStop(0, 'rgba(255, 218, 118, 0.7)'); // Yellow - increased
      gradient2.addColorStop(0.5, 'rgba(243, 156, 94, 0.4)'); // Orange - increased
      gradient2.addColorStop(1, 'rgba(243, 156, 94, 0)');

      ctx.fillStyle = gradient2;
      ctx.fillRect(0, 0, width, height);

      // Tertiary orb - counter-rotates
      const angle2 = -time * 1.2;
      const orb3X = centerX + Math.cos(angle2) * size * 0.2;
      const orb3Y = centerY + Math.sin(angle2) * size * 0.2;

      const gradient3 = ctx.createRadialGradient(
        orb3X,
        orb3Y,
        size * 0.03,
        orb3X,
        orb3Y,
        size * 0.2
      );
      gradient3.addColorStop(0, 'rgba(73, 161, 167, 0.65)'); // Teal - increased
      gradient3.addColorStop(0.5, 'rgba(73, 161, 167, 0.35)'); // increased
      gradient3.addColorStop(1, 'rgba(73, 161, 167, 0)');

      ctx.fillStyle = gradient3;
      ctx.fillRect(0, 0, width, height);

      // Ring effect - pulsing ring around the center
      const ringRadius = size * 0.3 + Math.sin(time * 1.5) * size * 0.05;
      const ringGradient = ctx.createRadialGradient(
        centerX,
        centerY,
        ringRadius - size * 0.02,
        centerX,
        centerY,
        ringRadius + size * 0.02
      );
      ringGradient.addColorStop(0, 'rgba(255, 218, 118, 0)');
      ringGradient.addColorStop(0.5, 'rgba(255, 218, 118, 0.35)'); // increased
      ringGradient.addColorStop(1, 'rgba(255, 218, 118, 0)');

      ctx.fillStyle = ringGradient;
      ctx.fillRect(0, 0, width, height);

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ opacity: 0.85 }}
    />
  );
};
